import { useGlobalState } from '@highlight/ipc/react'
import UpdaterIpcRenderer from '@renderer/ipc/UpdaterIpc.renderer'
import { fetchLatestPublishedChangelog } from '@renderer/windows/features/changelogs/atoms'
import { UpdateState } from '@shared/types/UpdateTypes'
import { useEffect, useMemo, useState } from 'react'

export const useUpdateState = () => {
  const [updateState] = useGlobalState<UpdateState>('update.state')
  const [, setDismissedAt] = useGlobalState<number>('update.dismissed')

  // The version of the incoming update (present once an update is available/ready/etc.).
  const incomingVersion = updateState && 'update' in updateState ? updateState.update?.version : undefined

  // The changelog's optional one-liner for the incoming update version. Not every changelog has
  // one, so this stays null when absent.
  const [oneLiner, setOneLiner] = useState<string | null>(null)

  useEffect(() => {
    if (!incomingVersion) {
      setOneLiner(null)
      return
    }
    let cancelled = false
    fetchLatestPublishedChangelog(incomingVersion)
      .then((changelog) => {
        if (!cancelled) {
          setOneLiner(changelog?.one_liner ?? null)
        }
      })
      .catch((error) => {
        console.warn('Failed to fetch changelog one-liner for update version', incomingVersion, error)
        if (!cancelled) {
          setOneLiner(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [incomingVersion])

  const onDownload = () => {
    UpdaterIpcRenderer.downloadUpdate()
  }

  const onInstall = async () => {
    const hasUpdate = await UpdaterIpcRenderer.checkForUpdates({ mode: 'install-cta' })
    if (!hasUpdate) {
      UpdaterIpcRenderer.quitAndInstall()
    }
  }

  const onClickCTA = () => {
    if (updateState?.state === 'available') {
      onDownload()
    } else if (updateState?.state === 'ready') {
      onInstall()
    } else if (updateState?.state === 'error') {
      window.open('https://highlightai.com/support', '_blank', 'noopener,noreferrer')
    }
  }

  const onDismiss = () => {
    setDismissedAt(Date.now())
  }

  const labels = useMemo(() => {
    if (!updateState) {
      return null
    }

    const oneLinerSuffix = oneLiner ? `: ${oneLiner}` : ''

    switch (updateState.state) {
      case 'checking':
        return {
          title: 'Checking for updates...',
          cta: 'Checking...',
        }
      case 'available':
        return {
          title: `A new version of the app is available${oneLinerSuffix}`,
          cta: 'Download Update',
        }
      case 'downloading':
        return {
          title: `A new version of the app is downloading${oneLinerSuffix}`,
          cta: `Downloading (${(updateState.progress?.percent ?? 0).toFixed(0)}%)`,
        }
      case 'ready':
        return {
          title: `A new version of the app is ready to install${oneLinerSuffix}`,
          cta: 'Install Update',
        }
      case 'installing':
        return {
          title: `A new version of the app is installing${oneLinerSuffix}`,
          cta: 'Installing...',
        }
      case 'error':
        return {
          title: 'Update Error',
          cta: 'Get Help',
        }
    }
  }, [updateState, oneLiner])

  return {
    update: updateState,
    labels,
    oneLiner,
    onDownload,
    onInstall,
    onClickCTA,
    onDismiss,
  }
}
